import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { DownloadIcon } from "lucide-react";
import { Assignment } from "@mui/icons-material";
import { NoAssignees } from "./StreamTableRow";
import { TableRowCommentsCounts } from "./TableRowCommentsCount";
import { TableRowScannedComments } from "./TableRowScannedComments";
import { TableRowAssignees } from "./TableRowAssignees";
import { CanvaLinkButton } from "./CanvaLinkButton";
import { MemoryBookStatusChip } from "./MemoryBookStatusChip";
import { User } from "../../../../users-admin/types/User";

interface MemoryBookStreamInfo {
  is_delivered: boolean;
  is_memory_book_available: boolean;
  approved_comments: number;
  total_comments: number;
  canva_memory_book_link: string | null;
  assignees: User[];
  created_at: string;
  last_updated_by: User;
  name_of_deceased: string;
  stream_date: string;
  stream_id: number;
  updated_at: string;
  physical_comments?: number;
  done_comments?: number;
  due_date?: string;
  booking_service?: string | null;
}

interface Props {
  row: MemoryBookStreamInfo;
  onRowClick: (row: MemoryBookStreamInfo) => void;
  onDownloadClick: (name: string) => void;
  onAssignClick: (row: MemoryBookStreamInfo) => void;
}

export const MobileView: React.FC<Props> = ({
  row,
  onRowClick,
  onDownloadClick,
  onAssignClick,
}) => {
  const dateObj = new Date(row.stream_date);
  const dayWeekday = dateObj
    .toLocaleDateString("en-GB", { weekday: "long", day: "numeric" })
    .replace(",", "")
    .toUpperCase();
  const monthYear = dateObj
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    .toUpperCase();

  return (
    <Card
      variant="outlined"
      onClick={() => onRowClick(row)}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        cursor: "pointer",
        "&:active": { transform: "scale(0.99)" },
        transition: "transform 0.1s ease",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3 }}
          >
            {row.name_of_deceased}
          </Typography>
          <MemoryBookStatusChip
            deliveredStatus={row.is_delivered}
            isBookAvailable={row.is_memory_book_available}
          />
        </Box>

        <Typography sx={{ fontSize: "0.78rem", opacity: 0.7, mb: 2 }}>
          {dayWeekday} {monthYear}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.55rem",
                fontWeight: 700,
                opacity: 0.6,
                letterSpacing: "0.5px",
                mb: 0.5,
              }}
            >
              APPROVED COMMENTS
            </Typography>
            <TableRowCommentsCounts
              approvedComments={row.approved_comments}
              allComments={row.total_comments}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "0.55rem",
                fontWeight: 700,
                opacity: 0.6,
                letterSpacing: "0.5px",
                mb: 0.5,
              }}
            >
              SCANNED COMMENTS
            </Typography>
            <TableRowScannedComments
              doneComments={row.done_comments || 0}
              physicalComments={row.physical_comments || 0}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Footer Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {row.assignees.length === 0 ? (
              <NoAssignees size={28} />
            ) : (
              <TableRowAssignees assignees={row.assignees} />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CanvaLinkButton canvaLink={row.canva_memory_book_link} />
            <Tooltip title="Download Thank you graphic">
              <IconButton
                color="primary"
                sx={{ width: 44, height: 44 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadClick(row.name_of_deceased);
                }}
              >
                <DownloadIcon size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Assign memory book">
              <IconButton
                color="secondary"
                sx={{ width: 44, height: 44 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignClick(row);
                }}
              >
                <Assignment />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
